/**
 * F41 (Wave 2 Track D — admin) — organizer approve / suspend dialog.
 *
 * Gated by the organizers page on `tournaments:approve_organizer`.
 *   - mode='approve' → optional organizer-type <Select> + mandatory reason →
 *     POST /admin/organizers/:b2bAccountId/approve  (ApproveOrganizerInputSchema)
 *   - mode='suspend' → mandatory reason →
 *     POST /admin/organizers/:b2bAccountId/suspend
 *
 * Composes F02 primitives only (Modal / FormField / Select / Textarea / Button /
 * Banner). Reason validated 1–500 chars against ApproveOrganizerInputSchema.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Select, Textarea } from '@bravemove/ui-web';
import { ApproveOrganizerInputSchema, ORGANIZER_TYPES, type OrganizerType } from '@bravemove/types';

export interface OrganizerDecisionDialogProps {
  readonly open: boolean;
  readonly mode: 'approve' | 'suspend';
  readonly onApprove: (input: { reason: string; organizerType?: OrganizerType }) => Promise<void>;
  readonly onSuspend: (reason: string) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

export function OrganizerDecisionDialog({
  open,
  mode,
  onApprove,
  onSuspend,
  onClose,
  errorMessage,
}: OrganizerDecisionDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [organizerType, setOrganizerType] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setReason('');
    setOrganizerType('');
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Both paths require a non-empty 1–500 char reason (ApproveOrganizerInputSchema
  // covers the bound; suspend reuses the same constraint).
  const valid = useMemo(() => ApproveOrganizerInputSchema.safeParse({ reason }).success, [reason]);

  const typeOptions = useMemo(
    () => [
      {
        value: '',
        label: t('admin:tournament.organizers.typeLabel', {
          defaultValue: 'Organizer type (optional)',
        }),
      },
      ...ORGANIZER_TYPES.map((ot) => ({
        value: ot,
        label: t(`tournament:organizer_type.${ot}`, { defaultValue: ot }),
      })),
    ],
    [t],
  );

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      if (mode === 'approve') {
        await onApprove({
          reason: reason.trim(),
          ...(organizerType ? { organizerType: organizerType as OrganizerType } : {}),
        });
      } else {
        await onSuspend(reason.trim());
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isApprove = mode === 'approve';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        isApprove
          ? t('admin:tournament.organizers.approveDialogTitle', {
              defaultValue: 'Approve this organizer?',
            })
          : t('admin:tournament.organizers.suspendDialogTitle', {
              defaultValue: 'Suspend this organizer?',
            })
      }
      description={
        isApprove
          ? t('admin:tournament.organizers.approveDialogBody', {
              defaultValue: 'An approved organizer can publish tournaments for free with no quota.',
            })
          : t('admin:tournament.organizers.suspendDialogBody', {
              defaultValue:
                'A suspended organizer cannot publish new tournaments. Their existing tournaments are hidden.',
            })
      }
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="organizer-decision-cancel"
          >
            {t('admin:tournament.organizers.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant={isApprove ? 'primary' : 'destructive'}
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="organizer-decision-submit"
          >
            {isApprove
              ? t('admin:tournament.organizers.approveConfirm', { defaultValue: 'Approve' })
              : t('admin:tournament.organizers.suspendConfirm', { defaultValue: 'Suspend' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="organizer-decision-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}
        {isApprove ? (
          <FormField
            label={t('admin:tournament.organizers.typeLabel', {
              defaultValue: 'Organizer type (optional)',
            })}
          >
            <Select
              options={typeOptions}
              value={organizerType}
              onChange={(e) => setOrganizerType(e.target.value)}
              data-testid="organizer-decision-type"
            />
          </FormField>
        ) : null}
        <FormField
          label={t('admin:tournament.organizers.reasonLabel', { defaultValue: 'Reason' })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('admin:tournament.organizers.reasonPlaceholder', {
              defaultValue: 'Reason for this decision',
            })}
            rows={3}
            maxLength={500}
            data-testid="organizer-decision-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}
